import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Users, 
  Plus, 
  Search, 
  Filter,
  Edit2,
  Trash2,
  GraduationCap,
  Calendar,
  User,
  X,
  AlertCircle,
  ArrowLeft,
  Home
} from 'lucide-react'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { api } from '../lib/axios'

interface Student {
  id: string
  firstName: string
  lastName: string
  birthdate: string | null
  gender: string | null
  classId: string
  class?: {
    name: string
    level?: string
  }
}

interface Class {
  id: string
  name: string
  level?: string
  year?: string
}

export function StudentsPage() {
  const navigate = useNavigate()
  const [students, setStudents] = useState<Student[]>([])
  const [classes, setClasses] = useState<Class[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterClassId, setFilterClassId] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'name' | 'class' | 'birthdate'>('name')
  const [showModal, setShowModal] = useState(false)
  const [editingStudent, setEditingStudent] = useState<Student | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [studentsResponse, classesResponse] = await Promise.all([
        api.get('/api/students'),
        api.get('/api/classes')
      ])
      
      // 🔧 FIX: Gérer le format de réponse API
      const studentsData = Array.isArray(studentsResponse.data) 
        ? studentsResponse.data 
        : studentsResponse.data.students || []
      
      const classesData = Array.isArray(classesResponse.data)
        ? classesResponse.data
        : classesResponse.data.classes || []
      
      console.log('📊 Students loaded:', studentsData)
      console.log('📊 Classes loaded:', classesData)
      
      setStudents(studentsData)
      setClasses(classesData)
    } catch (error) {
      console.error('Erreur chargement:', error)
    } finally {
      setLoading(false)
    }
  }

  // Filtrage et tri
  const filteredStudents = students
    .filter(student => {
      // 🔧 FIX: Ajout de vérifications nullish pour éviter "Cannot read properties of undefined"
      const matchSearch = 
        (student.firstName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (student.lastName?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      
      const matchClass = 
        filterClassId === 'all' || student.classId === filterClassId
      
      return matchSearch && matchClass
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = `${a.lastName || ''} ${a.firstName || ''}`
        const nameB = `${b.lastName || ''} ${b.firstName || ''}`
        return nameA.localeCompare(nameB)
      }
      if (sortBy === 'class') {
        // 🔧 FIX: Protection contre les propriétés class manquantes
        return (a.class?.name || '').localeCompare(b.class?.name || '')
      }
      if (sortBy === 'birthdate') {
        if (!a.birthdate) return 1
        if (!b.birthdate) return -1
        return new Date(a.birthdate).getTime() - new Date(b.birthdate).getTime()
      }
      return 0
    })

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/api/students/${id}`)
      setStudents(students.filter(s => s.id !== id))
      setDeleteConfirm(null)
    } catch (error) {
      console.error('Erreur suppression:', error)
      alert('Erreur lors de la suppression')
    }
  }

  const openAddModal = () => {
    setEditingStudent(null)
    setShowModal(true)
  }

  const openEditModal = (student: Student) => {
    setEditingStudent(student)
    setShowModal(true)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement des élèves...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Boutons de navigation */}
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
              title="Accueil"
            >
              <Home className="w-4 h-4" />
            </button>
          </div>

          {/* Titre et actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Mes Élèves</h1>
                <p className="text-gray-600 mt-1">
                  {filteredStudents.length} élève{filteredStudents.length > 1 ? 's' : ''}
                  {filterClassId !== 'all' && ' dans cette classe'}
                </p>
              </div>
            </div>
            <Button
              onClick={openAddModal}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Nouvel élève
            </Button>
          </div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un élève..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Filtre classe */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterClassId}
                onChange={(e) => setFilterClassId(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">Toutes les classes</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} {cls.level && `- ${cls.level}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Tri */}
            <div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="name">Trier par nom</option>
                <option value="class">Trier par classe</option>
                <option value="birthdate">Trier par âge</option>
              </select>
            </div>
          </div>
        </Card>

        {/* Liste élèves */}
        {filteredStudents.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filterClassId !== 'all' 
                ? 'Aucun élève trouvé' 
                : 'Aucun élève pour le moment'
              }
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filterClassId !== 'all'
                ? 'Essayez de modifier vos filtres'
                : 'Commencez par ajouter votre premier élève'
              }
            </p>
            {!searchTerm && filterClassId === 'all' && (
              <Button
                onClick={openAddModal}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold px-6 py-3 rounded-lg"
              >
                <Plus className="w-5 h-5 inline mr-2" />
                Ajouter un élève
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStudents.map(student => (
              <StudentCard
                key={student.id}
                student={student}
                onEdit={() => openEditModal(student)}
                onDelete={() => setDeleteConfirm(student.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal Add/Edit */}
      {showModal && (
        <StudentModal
          student={editingStudent}
          classes={classes}
          onClose={() => setShowModal(false)}
          onSave={() => {
            loadData()
            setShowModal(false)
          }}
        />
      )}

      {/* Modal Delete Confirmation */}
      {deleteConfirm && (
        <DeleteConfirmModal
          student={students.find(s => s.id === deleteConfirm)!}
          onConfirm={() => handleDelete(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  )
}

// Composant Card Élève
function StudentCard({ 
  student, 
  onEdit, 
  onDelete 
}: { 
  student: Student
  onEdit: () => void
  onDelete: () => void
}) {
  const age = student.birthdate 
    ? Math.floor((Date.now() - new Date(student.birthdate).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
    : null

  return (
    <Card className="p-6 hover:shadow-lg transition-shadow group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white font-bold text-lg">
            {student.firstName[0]}{student.lastName[0]}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {student.firstName} {student.lastName}
            </h3>
            {student.class && (
              <p className="text-sm text-gray-600 flex items-center gap-1">
                <GraduationCap className="w-4 h-4" />
                {student.class.name}
              </p>
            )}
          </div>
        </div>
        
        {/* Actions au survol */}
        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onEdit}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Modifier"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Infos supplémentaires */}
      <div className="space-y-2 text-sm text-gray-600">
        {age !== null && (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{age} ans</span>
          </div>
        )}
        {student.gender && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{student.gender === 'M' || student.gender === 'Masculin' ? 'Masculin' : 'Féminin'}</span>
          </div>
        )}
      </div>

      {/* Lien vers page détail (futur) */}
      <Link
        to={`/students/${student.id}`}
        className="mt-4 block text-center py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium text-sm"
      >
        Voir le profil →
      </Link>
    </Card>
  )
}

// Modal Add/Edit Student
function StudentModal({
  student,
  classes,
  onClose,
  onSave
}: {
  student: Student | null
  classes: Class[]
  onClose: () => void
  onSave: () => void
}) {
  const [formData, setFormData] = useState({
    firstName: student?.firstName || '',
    lastName: student?.lastName || '',
    birthdate: student?.birthdate || '',
    gender: student?.gender || '',
    classId: student?.classId || ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (student) {
        // Update (PUT)
        await api.put(`/api/students/${student.id}`, formData)
      } else {
        // Create (POST) - 🔧 FIX: Envoyer objet direct
        await api.post('/api/students', formData)
      }
      onSave()
    } catch (err: any) {
      console.error('❌ Erreur sauvegarde:', err)
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {student ? 'Modifier l\'\u00e9l\u00e8ve' : 'Nouvel \u00e9l\u00e8ve'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Nom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom *
            </label>
            <input
              type="text"
              required
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Dupont"
            />
          </div>

          {/* Prénom */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prénom *
            </label>
            <input
              type="text"
              required
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Jean"
            />
          </div>

          {/* Classe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Classe *
            </label>
            <select
              required
              value={formData.classId}
              onChange={(e) => setFormData({ ...formData, classId: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">Sélectionnez une classe</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>
                  {cls.name} {cls.level && `- ${cls.level}`}
                </option>
              ))}
            </select>
          </div>

          {/* Date de naissance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de naissance
            </label>
            <input
              type="date"
              value={formData.birthdate}
              onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Genre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Genre
            </label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">Non spécifié</option>
              <option value="Masculin">Masculin</option>
              <option value="Féminin">Féminin</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-lg transition-colors"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold py-2.5 rounded-lg transition-all"
            >
              {loading ? 'Sauvegarde...' : student ? 'Modifier' : 'Ajouter'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

// Modal Delete Confirmation
function DeleteConfirmModal({
  student,
  onConfirm,
  onCancel
}: {
  student: Student
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <div className="p-6">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
            Supprimer l'élève ?
          </h3>
          <p className="text-gray-600 text-center mb-6">
            Voulez-vous vraiment supprimer <strong>{student.firstName} {student.lastName}</strong> ?
            <br />
            <span className="text-sm text-red-600 mt-2 block">
              ⚠️ Toutes ses évaluations seront également supprimées.
            </span>
          </p>
          <div className="flex gap-3">
            <Button
              onClick={onCancel}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-lg transition-colors"
            >
              Annuler
            </Button>
            <Button
              onClick={onConfirm}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg transition-all"
            >
              Supprimer
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
